import { useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  MdOutlineAdd,
  MdOutlineCardGiftcard,
  MdOutlineClose,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { EVENTS } from "../../config/apiConfig";
import Modal from "../modals/Modal";
import { describeGiftRemaining } from "./eventHelpers";

/*
  Brindes de um evento, numa janela própria (botão "Brindes" ao lado de "Editar").

  O evento cadastra os brindes com o estoque que chegou e liga cada um a planos.
  `Estoque` é o total que chegou ao estande; o saldo cai sozinho a cada plano pago
  (por isso na edição aparece "já saíram" e "hoje restam", que o servidor
  calcula). Cada regra vale por plano, por ciclo ou pelos dois ("Pro" + "Anual",
  só "Pro", só "Anual"); todas as regras que servem ao pagamento valem juntas.
  Tirar um brinde da lista só o desativa no servidor (o que já foi entregue fica
  registrado).

  Salva só os brindes: `PUT /events/:id` com `{gifts}` não mexe no resto do
  evento. O servidor recebe a lista inteira (quem sumiu é desativado).
*/

const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";
const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const EventGiftsModal = ({ event, onClose, onSaved }) => {
  const [error, setError] = useState(null);
  // `key` só serve de chave do React (um item novo ainda não tem id).
  const nextKey = useRef(0);
  const newKey = () => {
    nextKey.current += 1;
    return `novo-${nextKey.current}`;
  };
  const [gifts, setGifts] = useState(() =>
    (event?.gifts || []).map((gift) => ({
      key: `gift-${gift.id}`,
      id: gift.id,
      name: gift.name,
      stock: String(gift.stock),
      delivered: gift.delivered,
      remaining: gift.remaining,
      rules: gift.rules.map((rule) => ({
        key: `rule-${rule.id}`,
        planId: rule.planId || "",
        billingCycle: rule.billingCycle || "",
        quantity: String(rule.quantity),
      })),
    }))
  );
  const [newGift, setNewGift] = useState("");

  const options = useQuery(
    ["events", "options"],
    async () => {
      const { data } = await axios.get(EVENTS.OPTIONS);
      return data.data;
    },
    // Sempre confere ao abrir: uma resposta guardada de antes dos brindes não
    // traz os planos e ciclos das regras.
    { staleTime: 60_000, refetchOnMount: "always" }
  );
  const plans = options.data?.giftRules?.plans || [];
  const cycles = options.data?.giftRules?.billingCycles || [];
  // Servidor que ainda não conhece os brindes responde sem planos e ciclos.
  const optionsMissing = !options.isLoading && !plans.length;

  const save = useMutation(
    () =>
      axios.put(`${EVENTS.ONE}/${event.id}`, {
        gifts: gifts.map((gift) => ({
          id: gift.id,
          name: gift.name,
          stock: Number(gift.stock) || 0,
          rules: gift.rules.map((rule) => ({
            planId: rule.planId || null,
            billingCycle: rule.billingCycle || null,
            quantity: Number(rule.quantity) || 0,
          })),
        })),
      }),
    {
      onSuccess: ({ data }) => onSaved?.(data.data),
      onError: (err) =>
        setError(err.response?.data?.message || "Não foi possível salvar os brindes."),
    }
  );

  const addGift = () => {
    const typed = newGift.trim();
    if (!typed) return;

    const repeated = gifts.some(
      (gift) => gift.name.trim().toLowerCase() === typed.toLowerCase()
    );
    if (repeated) {
      setError(`O brinde "${typed}" já está na lista.`);
      return;
    }

    setError(null);
    setGifts([
      ...gifts,
      { key: newKey(), id: null, name: typed, stock: "0", rules: [] },
    ]);
    setNewGift("");
  };

  const patchGift = (key, patch) =>
    setGifts(gifts.map((gift) => (gift.key === key ? { ...gift, ...patch } : gift)));

  const patchRule = (giftKey, ruleKey, patch) =>
    setGifts(
      gifts.map((gift) =>
        gift.key === giftKey
          ? {
              ...gift,
              rules: gift.rules.map((rule) =>
                rule.key === ruleKey ? { ...rule, ...patch } : rule
              ),
            }
          : gift
      )
    );

  // Regra precisa de plano, de ciclo ou dos dois, e de uma quantidade.
  const giftsProblem = gifts.some((gift) =>
    gift.rules.some(
      (rule) =>
        (!rule.planId && !rule.billingCycle) || !(Number(rule.quantity) >= 1)
    )
  )
    ? "Toda regra de brinde precisa de um plano, de um ciclo ou dos dois, e de uma quantidade."
    : null;

  const canSave = !giftsProblem && !save.isLoading;

  return (
    <Modal
      isOpen
      onClose={() => (save.isLoading ? null : onClose())}
      title="Brindes do evento"
      subtitle={event.name}
      icon={MdOutlineCardGiftcard}
      size="lg"
      closeOnBackdrop={false}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={save.isLoading}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={!canSave}
            className="rounded-xl bg-pink-600 px-5 py-2 text-sm font-bold text-white hover:bg-pink-700 disabled:opacity-50"
          >
            {save.isLoading ? "Salvando..." : "Salvar brindes"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <fieldset>
          <legend className={labelClass}>Brindes</legend>

          <div className="flex items-center gap-2">
            <input
              id="event-new-gift"
              value={newGift}
              maxLength={120}
              placeholder="Nome do brinde (ex.: Caneca)"
              onChange={(e) => setNewGift(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addGift();
                }
              }}
              className={fieldClass}
            />
            <button
              type="button"
              onClick={addGift}
              aria-label="Adicionar brinde"
              disabled={!newGift.trim()}
              className="shrink-0 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Adicionar
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            {gifts.length
              ? "O estoque diminui sozinho quando o pagamento do plano entra. Se acabar, a cobrança avisa antes e a equipe decide."
              : "Sem brindes, o evento não controla estoque. Cadastre para dar a baixa a cada plano pago."}
          </p>

          {gifts.length ? (
            <ul className="mt-3 space-y-3">
              {gifts.map((gift) => (
                <li
                  key={gift.key}
                  className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3"
                >
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <span className={labelClass}>Brinde</span>
                      <input
                        value={gift.name}
                        maxLength={120}
                        aria-label="Nome do brinde"
                        onChange={(e) => patchGift(gift.key, { name: e.target.value })}
                        className={fieldClass}
                      />
                    </div>
                    <div className="w-28 shrink-0">
                      <span className={labelClass}>Estoque</span>
                      <input
                        type="number"
                        min="0"
                        max="1000000"
                        inputMode="numeric"
                        value={gift.stock}
                        aria-label={`Estoque de ${gift.name || "brinde"}`}
                        onChange={(e) => patchGift(gift.key, { stock: e.target.value })}
                        className={fieldClass}
                      />
                    </div>
                    <button
                      type="button"
                      aria-label={`Tirar ${gift.name || "brinde"}`}
                      title="Tirar da lista"
                      onClick={() => setGifts(gifts.filter((item) => item.key !== gift.key))}
                      className="mb-0.5 shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                    >
                      <MdOutlineClose size={18} />
                    </button>
                  </div>

                  {gift.id ? (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Já saíram {gift.delivered}. O estoque é o total que chegou ao
                      evento, não o que sobra: hoje {describeGiftRemaining(gift.remaining)}.
                    </p>
                  ) : null}

                  <div className="mt-3">
                    <p className={labelClass}>Quem ganha este brinde</p>
                    {gift.rules.length ? (
                      <div
                        aria-hidden="true"
                        className="mb-1 grid grid-cols-[1fr_1fr_5.5rem_2.25rem] gap-2 text-[11px] font-semibold text-slate-400"
                      >
                        <span>Plano</span>
                        <span>Ciclo</span>
                        <span title="Quantas unidades cada cliente ganha">
                          Qtd. por cliente
                        </span>
                        <span />
                      </div>
                    ) : null}
                    {gift.rules.length ? (
                      <ul className="space-y-2">
                        {gift.rules.map((rule) => (
                          <li
                            key={rule.key}
                            className="grid grid-cols-[1fr_1fr_5.5rem_2.25rem] items-center gap-2"
                          >
                            <select
                              aria-label="Plano"
                              value={rule.planId}
                              onChange={(e) =>
                                patchRule(gift.key, rule.key, { planId: e.target.value })
                              }
                              className={fieldClass}
                            >
                              <option value="">Qualquer plano</option>
                              {plans.map((plan) => (
                                <option key={plan.id} value={plan.id}>
                                  {plan.name}
                                </option>
                              ))}
                            </select>
                            <select
                              aria-label="Ciclo"
                              value={rule.billingCycle}
                              onChange={(e) =>
                                patchRule(gift.key, rule.key, {
                                  billingCycle: e.target.value,
                                })
                              }
                              className={fieldClass}
                            >
                              <option value="">Qualquer ciclo</option>
                              {cycles.map((cycle) => (
                                <option key={cycle.id} value={cycle.id}>
                                  {cycle.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              min="1"
                              max="1000"
                              inputMode="numeric"
                              aria-label="Quantidade por cliente"
                              title="Quantas unidades cada cliente ganha"
                              value={rule.quantity}
                              onChange={(e) =>
                                patchRule(gift.key, rule.key, { quantity: e.target.value })
                              }
                              className={fieldClass}
                            />
                            <button
                              type="button"
                              aria-label="Tirar regra"
                              title="Tirar regra"
                              onClick={() =>
                                patchGift(gift.key, {
                                  rules: gift.rules.filter((item) => item.key !== rule.key),
                                })
                              }
                              className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                            >
                              <MdOutlineClose size={18} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-slate-500">
                        Sem regra: nenhum plano dá este brinde ainda.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        patchGift(gift.key, {
                          rules: [
                            ...gift.rules,
                            { key: newKey(), planId: "", billingCycle: "", quantity: "1" },
                          ],
                        })
                      }
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-pink-700 hover:underline"
                    >
                      <MdOutlineAdd size={14} /> Adicionar regra
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          {gifts.some((gift) => gift.rules.length) ? (
            <p className="mt-2 text-[11px] text-slate-500">
              Cada regra vale por plano, por ciclo ou pelos dois, e a quantidade é
              quantas unidades cada cliente ganha (ex.: Pro, Anual, 2 = quem paga
              o Pro anual ganha 2). Se mais de uma regra servir ao mesmo
              pagamento, a cliente ganha todas.
            </p>
          ) : null}
          {optionsMissing ? (
            <p role="alert" className="mt-2 text-xs font-semibold text-rose-700">
              Não foi possível carregar os planos e os ciclos para as regras. Se o
              servidor acabou de ser atualizado, reinicie o backend e abra esta
              janela de novo.
            </p>
          ) : null}
          {giftsProblem ? (
            <p role="alert" className="mt-2 text-xs font-semibold text-amber-700">
              {giftsProblem}
            </p>
          ) : null}
        </fieldset>

        {error ? (
          <p
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default EventGiftsModal;
